import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, WhatsappLogo, Phone } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { whatsappLink, telLink, BRAND } from "@/lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      {/* Top info strip */}
      <div className="hidden bg-ink text-offwhite md:block">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-8 py-2 text-xs">
          <span className="tracking-wide text-white/70">
            Custom Glass & Aluminium Solutions · Ahmedabad · Since 15+ Years
          </span>
          <div className="flex items-center gap-5">
            <a href={telLink()} className="flex items-center gap-2 text-white/80 hover:text-white">
              <Phone weight="fill" size={13} /> {BRAND.phoneDisplay}
            </a>
            <a href={`mailto:${BRAND.email}`} className="text-white/80 hover:text-white">
              {BRAND.email}
            </a>
          </div>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-offwhite/90 backdrop-blur-xl shadow-[0_1px_0_rgba(0,0,0,0.06)]"
            : "bg-offwhite/70 backdrop-blur-md border-b border-black/5"
        }`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-5 py-4 md:px-8 md:py-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Nagar Glass"
              className="h-10 w-10 shrink-0 object-contain md:h-11 md:w-11"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-display text-lg font-semibold tracking-wide text-ink md:text-xl">
                Nagar Glass
              </span>
              <span className="text-[0.62rem] uppercase tracking-[0.22em] text-muted-foreground">
                Glass & Aluminium · Ahmedabad
              </span>
            </div>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-[0.82rem] font-medium tracking-wide transition-colors duration-300 hover:text-ink ${
                    isActive ? "text-ink" : "text-muted-foreground"
                  } ${isActive ? "after:absolute after:-bottom-1.5 after:left-0 after:right-0 after:h-px after:bg-gold" : ""}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="hidden items-center gap-2 rounded-sm bg-ink px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-offwhite transition-colors duration-500 hover:bg-gold hover:text-ink md:inline-flex"
            >
              <WhatsappLogo weight="fill" size={15} />
              WhatsApp
            </a>
            <button
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-sm border border-black/15 text-ink lg:hidden"
            >
              {open ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden border-t border-black/10 bg-offwhite lg:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col gap-1 px-5 pb-8 pt-3">
                {NAV.map((n, i) => (
                  <motion.div
                    key={n.to}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                  >
                    <NavLink
                      to={n.to}
                      data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                      className="block border-b border-black/10 py-4 font-display text-2xl font-medium text-ink"
                    >
                      {n.label}
                    </NavLink>
                  </motion.div>
                ))}
                <div className="mt-5 flex flex-col gap-3">
                  <a
                    href={whatsappLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-sm bg-ink px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-offwhite"
                  >
                    <WhatsappLogo weight="fill" size={16} /> WhatsApp
                  </a>
                  <a
                    href={telLink()}
                    className="inline-flex items-center justify-center gap-2 rounded-sm border border-ink px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-ink"
                  >
                    <Phone weight="fill" size={16} /> Call {BRAND.phoneDisplay}
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
